import { Box, Tab } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import React, { useEffect } from "react";
import DotLegalTabPanel from "../dotLegalTabPanel/DotLegalTabPanel";
import { dotLegalTabStyles, useDotLegalTabsStyles } from "./DotLegalTabs.styles";
import { TabContent } from "./DotLegalTabs.types";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";

export interface IDotLegalTabs {
    tabStyle: dotLegalTabStyles;
    selectedTab: number;
    setSelectedTab: (index: number) => void;
    content: Array<TabContent>;
    fullWidth: boolean;
    ignoreTextTransformation?: boolean;
    noPadding?: boolean;
    border?: boolean;
    divider?: boolean;
    fillColumn?: boolean;
}

function TruncatedTabLabel({ label, tooltip }: { label: string; tooltip?: string }) {
    const textRef = React.useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    const [, forceUpdate] = React.useState(0);

    useEffect(() => {
        const onResize = () => forceUpdate((n) => n + 1);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // eslint-disable-next-line
    useEffect(() => {
        const el = textRef.current;
        if (el) {
            setIsOverflowing(el.scrollWidth > el.clientWidth);
        }
    });

    const getTooltipText = () => {
        if (isOverflowing && tooltip) {
            return `${label}\n\n${tooltip}`;
        }
        if (isOverflowing) {
            return label;
        }
        if (tooltip) {
            return tooltip;
        }
        return "";
    };

    return (
        <DotLegalTooltip text={getTooltipText()}>
            <span ref={textRef} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                {label}
            </span>
        </DotLegalTooltip>
    );
}

function DotLegalTabs(props: IDotLegalTabs) {
    const styles = useDotLegalTabsStyles(props);
    const isPaperTabs = props.tabStyle === "paper-tabs";
    const tabsRef = React.useRef<HTMLDivElement>(null);
    const [tabsFullWidth, setTabsFullWidth] = React.useState(false);

    useEffect(() => {
        if (!isPaperTabs || !tabsRef.current) return;

        const tabsRoot = tabsRef.current;
        const flexContainer = tabsRoot.querySelector(".MuiTabs-flexContainer") as HTMLElement | null;
        if (!flexContainer) return;

        const checkFullWidth = () => {
            setTabsFullWidth(flexContainer.scrollWidth >= tabsRoot.clientWidth);
        };

        const observer = new ResizeObserver(checkFullWidth);
        observer.observe(tabsRoot);
        observer.observe(flexContainer);

        return () => observer.disconnect();
    }, [isPaperTabs]);

    const getTabLabels = () => {
        const lastIndex = props.content.length - 1;

        return props.content.map((x, i) => {
            const className = i === 0 ? "topLeft" : lastIndex === i ? "topRight" : "";

            return (
                <Tab
                    className={className}
                    value={x.index}
                    key={x.index}
                    label={isPaperTabs ? <TruncatedTabLabel label={x.label} tooltip={x.tooltip} /> : getTabLabel(x.label, x.tooltip)}
                    icon={x.icon}
                />
            );
        });
    };

    const getTabLabel = (label: string, tooltip?: string | undefined) => {
        if (tooltip)
            return (
                <DotLegalTooltip text={tooltip}>
                    <span>{label}</span>
                </DotLegalTooltip>
            );

        return label;
    };

    const getTabContent = () => {
        return props.content.map((x) => {
            return (
                <DotLegalTabPanel
                    key={x.index}
                    index={x.index}
                    value={props.selectedTab}
                    noPadding={props.noPadding}
                    border={props.border}
                    fillColumn={props.fillColumn}
                    tabsFullWidth={isPaperTabs && tabsFullWidth}
                >
                    {x.content}
                </DotLegalTabPanel>
            );
        });
    };

    const Wrapper = props.fillColumn ? Box : React.Fragment;
    const wrapperProps = props.fillColumn ? { sx: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 } } : {};

    return (
        <Wrapper {...wrapperProps}>
            <Tabs
                ref={tabsRef}
                value={props.selectedTab}
                onChange={(event: React.ChangeEvent<{}>, newValue: number) => {
                    const tabContent = props.content.filter((c) => c.index === newValue);

                    if (tabContent && !tabContent[0].disabled) {
                        props.setSelectedTab(newValue);
                    }
                }}
                indicatorColor="primary"
                textColor="primary"
                variant={props.fullWidth ? "fullWidth" : "scrollable"}
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={styles.selectedTab}
            >
                {getTabLabels()}
            </Tabs>
            {getTabContent()}
        </Wrapper>
    );
}

export default DotLegalTabs;
